import { create } from "zustand";
import type { UserTrackRecord, AlphaTweet } from "@/types";
import { fetchLeaderboard, fetchAlphaFeed, type FeedParams } from "@/services/api";

interface LeaderboardState {
  // Leaderboard data (track records)
  trackRecords: UserTrackRecord[];
  leaderboardLoading: boolean;
  leaderboardError: string | null;
  hasMore: boolean;
  currentPage: number;

  // Feed data
  tweets: AlphaTweet[];
  feedLoading: boolean;
  feedError: string | null;

  // Track record lookup cache
  trackRecordMap: Map<string, UserTrackRecord>;

  // Filters
  minAlpha: number;
  minSignals: number;
  searchQuery: string;

  // Actions
  loadLeaderboard: (minSignals?: number) => Promise<void>;
  loadMoreLeaderboard: () => Promise<void>;
  loadFeed: (params?: FeedParams) => Promise<void>;
  setMinAlpha: (value: number) => void;
  setMinSignals: (value: number) => void;
  setSearchQuery: (query: string) => void;
  getTrackRecord: (handle: string) => UserTrackRecord | null;
  getFilteredRecords: () => UserTrackRecord[];
}

export const useLeaderboardStore = create<LeaderboardState>((set, get) => ({
  // Initial state
  trackRecords: [],
  leaderboardLoading: false,
  leaderboardError: null,
  hasMore: true,
  currentPage: 0,

  tweets: [],
  feedLoading: false,
  feedError: null,

  trackRecordMap: new Map(),

  minAlpha: 7,
  minSignals: 5,
  searchQuery: "",

  // Actions
  loadLeaderboard: async (minSignalsOverride?: number) => {
    const state = get();
    const minSignals = minSignalsOverride ?? state.minSignals;
    set({
      leaderboardLoading: true,
      leaderboardError: null,
      currentPage: 0,
      trackRecords: [],
      hasMore: true
    });

    try {
      const records = await fetchLeaderboard(minSignals, 0, 200);

      // Build lookup map
      const map = new Map<string, UserTrackRecord>();
      for (const record of records) {
        map.set(record.twitterHandle.toLowerCase(), record);
      }

      set({
        trackRecords: records,
        trackRecordMap: map,
        leaderboardLoading: false,
        currentPage: 1,
        hasMore: records.length === 200,
      });
    } catch (error) {
      set({
        leaderboardError:
          error instanceof Error ? error.message : "Failed to load leaderboard",
        leaderboardLoading: false,
      });
    }
  },

  loadMoreLeaderboard: async () => {
    const state = get();
    if (state.leaderboardLoading || !state.hasMore) return;

    set({ leaderboardLoading: true, leaderboardError: null });

    try {
      const offset = state.currentPage * 200;
      const records = await fetchLeaderboard(state.minSignals, offset, 200);

      // Add to existing records and map
      const map = new Map(state.trackRecordMap);
      for (const record of records) {
        map.set(record.twitterHandle.toLowerCase(), record);
      }

      set({
        trackRecords: [...state.trackRecords, ...records],
        trackRecordMap: map,
        leaderboardLoading: false,
        currentPage: state.currentPage + 1,
        hasMore: records.length === 200,
      });
    } catch (error) {
      set({
        leaderboardError:
          error instanceof Error ? error.message : "Failed to load more",
        leaderboardLoading: false,
      });
    }
  },

  loadFeed: async (params?: FeedParams) => {
    const state = get();
    set({ feedLoading: true, feedError: null });

    try {
      const tweets = await fetchAlphaFeed({
        minAlpha: state.minAlpha,
        ...params,
      });

      set({
        tweets,
        feedLoading: false,
      });
    } catch (error) {
      set({
        feedError:
          error instanceof Error ? error.message : "Failed to load feed",
        feedLoading: false,
      });
    }
  },

  setMinAlpha: (value: number) => {
    set({ minAlpha: value });
  },

  setMinSignals: (value: number) => {
    set({ minSignals: value });
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query });
  },

  getTrackRecord: (handle: string) => {
    const state = get();
    return state.trackRecordMap.get(handle.toLowerCase()) || null;
  },

  getFilteredRecords: () => {
    const state = get();
    const { trackRecords, searchQuery } = state;

    if (!searchQuery.trim()) {
      return trackRecords;
    }

    const query = searchQuery.toLowerCase().trim();
    return trackRecords.filter((record) =>
      record.twitterHandle.toLowerCase().includes(query)
    );
  },
}));
